import api from './api';

class TournamentLiveService {
  constructor() {
    this.pollingInterval = null;
    this.currentTournamentId = null;
    this.lastStats = null;
    this.listeners = new Set();
  }

  /**
   * Start live stats monitoring (WebSocket-based, no polling)
   * @param {number} tournamentId - Tournament ID to monitor
   */
  startPolling(tournamentId) {
    this.stopPolling();
    this.currentTournamentId = tournamentId;
    
    // Fetch initial data once
    this.fetchLiveStats(tournamentId);
    
    // No polling - WebSocket will handle updates
  }

  /**
   * Stop live stats monitoring
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.currentTournamentId = null;
  }

  /**
   * Update tournament info from WebSocket
   * @param {Object} tournamentInfo - Tournament info data
   */
  updateTournamentInfo(tournamentInfo) {
    if (this.lastStats) {
      // Update existing stats with new tournament info
      this.lastStats = {
        ...this.lastStats,
        tournament: {
          ...this.lastStats.tournament,
          ...tournamentInfo.tournament
        },
        currentRound: tournamentInfo.currentRound,
        currentHole: tournamentInfo.currentHole,
        totalSkins: tournamentInfo.totalSkins,
        totalSawoffs: tournamentInfo.totalSawoffs
      };
      
      // Notify listeners
      this.notifyListeners(this.lastStats);
    }

    // If this is a leaderboard update, also notify listeners with leaderboard data
    if (tournamentInfo.leaderboard && Array.isArray(tournamentInfo.leaderboard)) {
      console.log('📊 TournamentLiveService: Broadcasting leaderboard update to listeners');
      this.notifyListeners(this.lastStats, null, tournamentInfo);
    }
  }

  /**
   * Fetch live tournament statistics
   * @param {number} tournamentId - Tournament ID
   * @returns {Promise<Object>} Live tournament stats
   */
  async fetchLiveStats(tournamentId) {
    try {
      const response = await api.get(`/tournament-live/${tournamentId}`);
      const newStats = response.data;
      
      // Check for skin wins and notify listeners
      this.checkForSkinWins(newStats);
      
      // Update last stats and notify listeners
      this.lastStats = newStats;
      this.notifyListeners(newStats);
      
      return newStats;
    } catch (error) {
      console.error('Error fetching tournament live stats:', error);
      this.notifyListeners(null, error);
      return null;
    }
  }

  /**
   * Check for new skin wins and notify listeners
   * @param {Object} newStats - New tournament stats
   */
  checkForSkinWins(newStats) {
    if (!this.lastStats || !newStats) return;

    const lastPlayers = this.lastStats.purchasedPlayers || [];
    const newPlayers = newStats.purchasedPlayers || [];

    // Check each player for new skin wins
    newPlayers.forEach(newPlayer => {
      const lastPlayer = lastPlayers.find(p => p.playerId === newPlayer.playerId);
      
      if (lastPlayer) {
        const lastSkins = lastPlayer.skinsWon || 0;
        const newSkins = newPlayer.skinsWon || 0;
        
        // If player gained skins, find the new skin wins
        if (newSkins > lastSkins) {
          const newSkinWins = newPlayer.skinWins.slice(lastSkins);
          
          // Notify for each new skin win
          newSkinWins.forEach(skinWin => {
            this.notifySkinWin({
              playerName: newPlayer.playerName,
              round: skinWin.round,
              hole: skinWin.hole
            });
          });
        }
      }
    });
  }

  /**
   * Add listener for live stats updates
   * @param {Function} callback - Callback function to receive updates
   */
  addListener(callback) {
    this.listeners.add(callback);
    
    // If we have cached stats, send them immediately
    if (this.lastStats) {
      callback(this.lastStats);
    }
  }

  /**
   * Remove listener for live stats updates
   * @param {Function} callback - Callback function to remove
   */
  removeListener(callback) {
    this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of stats update
   * @param {Object} stats - Tournament stats
   * @param {Error} error - Error if any
   * @param {Object} leaderboardData - Leaderboard data if any
   */
  notifyListeners(stats, error = null, leaderboardData = null) {
    this.listeners.forEach(callback => {
      try {
        callback(stats, error, leaderboardData);
      } catch (err) {
        console.error('Error in stats listener:', err);
      }
    });
  }

  /**
   * Notify listeners of skin win
   * @param {Object} skinWin - Skin win data
   */
  notifySkinWin(skinWin) {
    this.listeners.forEach(callback => {
      try {
        if (callback.onSkinWin) {
          callback.onSkinWin(skinWin);
        }
      } catch (err) {
        console.error('Error in skin win listener:', err);
      }
    });
  }

  /**
   * Get current cached stats
   * @returns {Object|null} Current stats or null
   */
  getCurrentStats() {
    return this.lastStats;
  }

  /**
   * Check if currently monitoring
   * @returns {boolean} True if monitoring is active
   */
  isPolling() {
    return this.currentTournamentId !== null;
  }

  /**
   * Get current tournament ID being polled
   * @returns {number|null} Tournament ID or null
   */
  getCurrentTournamentId() {
    return this.currentTournamentId;
  }
}

// Create singleton instance
const tournamentLiveService = new TournamentLiveService();

export default tournamentLiveService;
